/**
 * The permanent top bar — the contract every DexCare.ai app renders identically.
 *
 * Anatomy: waffle · active-app chip · org mark/switcher · in-app breadcrumb
 * slot ····· host actions slot.
 *
 * Everything answering "where am I?" (which app, which organization) is grouped
 * on the left; the host's own actions — identity, help, product buttons — sit on
 * the right. The org mark shipped at the far right initially, which put it
 * immediately beside the host's user menu and read as part of the account menu.
 *
 * What this deliberately does NOT own: the consuming app's left navigation or its
 * content. The left nav belongs to the current app, not the platform.
 */
import { useEffect, useMemo, useRef, useState } from "react"
import { Icon, WaffleGlyph } from "./icons"
import { buildHref, hasHosts, needsOrg, switchHref, type HostMap, type Tile } from "./registry"
import { getOrg, orgLabel } from "./org"
import { TenantSwitcher } from "./TenantSwitcher"
import type { Tenant, TenantsFailure } from "./tenants"

export interface TopBarProps {
  /** Which app is rendering the shell — its tile is marked current. */
  app: string
  tiles: Tile[]
  hosts: HostMap
  /** True when the tile list came from the embedded seed rather than the platform. */
  degraded: boolean
  /** Offer a tile's destination to the host first. True means it routed itself,
   *  so the shell must not navigate — otherwise the in-place route is
   *  immediately thrown away by a full page load. */
  onNavigate?: (tile: Tile, href: string) => boolean
  /** Ask the host to switch organization. True when the host handled it, meaning
   *  the shell must not navigate itself. `target` is the tenant the user picked,
   *  or undefined when they only asked to see the full list. */
  onOrgSwitch?: (target?: Tenant) => boolean
  /** Whether the row still leaves for Optimize, which decides whether the
   *  external-link arrow is shown. */
  orgSwitchIsExternal?: boolean
  /** Every organization the user can reach. Empty means the switcher degrades to
   *  a link rather than disappearing. */
  tenants?: Tenant[]
  /** Why `tenants` is empty, when it is — drives what the panel says. */
  tenantsFailure?: TenantsFailure
}

function groupTiles(tiles: Tile[]) {
  const groups: { title: string; items: Tile[] }[] = []
  for (const t of tiles) {
    let g = groups.find((x) => x.title === t.group)
    if (!g) groups.push((g = { title: t.group, items: [] }))
    g.items.push(t)
  }
  return groups
}

export function TopBar({
  app,
  tiles,
  hosts,
  degraded,
  onNavigate,
  onOrgSwitch,
  orgSwitchIsExternal = true,
  tenants = [],
  tenantsFailure,
}: TopBarProps) {
  const [open, setOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const org = getOrg()

  // Close on outside click and on Escape. Within a shadow root, composedPath()
  // is what actually tells us whether the click was ours.
  useEffect(() => {
    if (!open) return
    const onDown = (e: Event) => {
      if (!e.composedPath().includes(panelRef.current as EventTarget)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("pointerdown", onDown, true)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("pointerdown", onDown, true)
      document.removeEventListener("keydown", onKey)
    }
  }, [open])

  const groups = useMemo(() => groupTiles(tiles), [tiles])
  const active = tiles.find((t) => t.key === app)

  // Two different reasons a tile cannot be followed, both of which disable it
  // rather than hiding it: the launcher keeps its shape, and "unavailable" is
  // honest where a click that silently went nowhere was not.
  //
  //   no org yet          — the host has not handed one over and the URL had none.
  //                         Temporary; resolves itself once the tenant loads.
  //   not in this env     — the app has no deployment in this environment.
  //                         Permanent until one exists. Deliberately NOT resolved
  //                         by falling back to production: a dev tile that opens
  //                         live production data is the leak the environment map
  //                         exists to prevent, and it is invisible because the
  //                         page loads and looks correct.
  const blocked = (tile: Tile) => (needsOrg(tile) && !org?.id) || !hasHosts(tile, hosts)

  const go = (tile: Tile) => {
    setOpen(false)
    if (tile.key === app) return // already here
    if (blocked(tile)) return
    let href: string
    try {
      href = buildHref(tile, org ?? {}, hosts)
    } catch (e) {
      // A misconfigured template must not navigate somewhere wrong. Failing
      // loudly in the console beats sending someone to a broken host, which is
      // how six tiles once pointed at a domain that did not exist.
      console.error("[dex-app-shell]", e)
      return
    }
    // The host routes in place if it can — a full page load to reach a route it
    // already serves is the cost this avoids. Declining leaves the original
    // behaviour untouched.
    if (onNavigate?.(tile, href)) return

    if (tile.isExternal) window.open(href, "_blank", "noopener,noreferrer")
    else window.location.assign(href)
  }

  /**
   * Switch tenant without leaving the app you are in.
   *
   * The host gets first refusal — a SPA that can re-route in place should, since
   * a full page load to change tenant is the thing this replaces. If it declines,
   * rebuild the CURRENT app's own href against the new tenant, using the same
   * per-app template the launcher uses. Same origin, same app, same page family;
   * only the tenant changes.
   *
   * Returns false when the shell genuinely cannot work out a destination, so the
   * menu can say so instead of closing as though something happened.
   */
  const switchTenant = (tenant: Tenant): boolean => {
    if (onOrgSwitch?.(tenant)) return true

    const href = switchHref(app, tiles, { id: tenant.id, name: tenant.name }, hosts)
    if (!href) {
      console.warn(
        `[dex-app-shell] cannot switch tenant in place: no usable destination for "${app}". ` +
          "Handle the dex-org-switch event, or register the app.",
      )
      return false
    }
    // Deliberately same-tab, and `isExternal` is not consulted: that flag
    // describes the tile as seen FROM Optimize. Seen from inside the app itself,
    // switching organization is same-origin navigation, not opening a second thing.
    window.location.assign(href)
    return true
  }

  /** The full organizations page — fallback, and where you go to manage them. */
  const seeAllOrgs = () => {
    if (onOrgSwitch?.()) return
    const optimize = (hosts.optimize || "").replace(/\/$/, "")
    window.open(`${optimize}/dashboard`, "_blank", "noopener,noreferrer")
  }

  return (
    <header className="dex-topbar">
      <div className="dex-topbar-waffle">
        <button
          type="button"
          className="dex-icon-btn"
          aria-label="Switch app"
          aria-expanded={open}
          aria-haspopup="menu"
          onClick={() => setOpen((v) => !v)}
        >
          <WaffleGlyph />
        </button>
      </div>

      {/* Divider lives INSIDE the conditional, not before it: an app key that is
          not in the registry renders no chip, and a bare divider against the
          waffle would leave two rules with nothing between them. */}
      {active && (
        <>
          <span className="dex-divider" />
          <span className="dex-active-app">
            <span className={`dex-tile-icon dex-tint-${active.color}`}>
              <Icon name={active.icon} />
            </span>
            <span className="dex-active-app-label">{active.label}</span>
          </span>
        </>
      )}

      {/* Separates "which app" from "which organization" — two different
          questions that were reading as one run of chips. Auth0 puts the same
          rule between its logo and its tenant picker. */}
      <span className="dex-divider" />

      {/* The org mark IS the switcher — one control, in the same place in every
          app, rather than a per-app tenant selector page.
          It sits in the LEFT cluster, beside the app chip, because both answer
          "where am I?" — which app, which organization. Identity ("who am I?")
          stays alone on the right. It shipped inside .dex-topbar-right at first,
          which put it immediately beside the host's user menu: two name-chips
          with circular badges, adjacent, the org one dead last, reading as an
          afterthought bolted onto the account menu. Scope-left / identity-right
          is also what GCP, Vercel, Stripe and Auth0 all do. */}
      <TenantSwitcher
        org={org}
        tenants={tenants}
        failure={tenantsFailure}
        onPick={switchTenant}
        onSeeAll={seeAllOrgs}
      />

      <span className="dex-divider" />
      {/* The host app owns its breadcrumb — the bar only reserves the space. */}
      <slot name="breadcrumb" />

      <div className="dex-topbar-right">
        {/* Host-specific actions (Optimize injects Copilot and its account
            drawer here). Kept as a slot so product features do not end up
            inside shared chrome, which is how they got there before. */}
        <slot name="topbar-actions" />
      </div>

      {open && (
        <div className="dex-waffle-panel" role="menu" ref={panelRef}>
          {groups.map((g) => (
            <div className="dex-waffle-group" key={g.title}>
              <div className="dex-waffle-group-title">{g.title}</div>
              <div className="dex-waffle-grid">
                {g.items.map((t) => {
                  const isCurrent = t.key === app
                  const isBlocked = blocked(t)
                  return (
                    <button
                      type="button"
                      key={t.key}
                      className="dex-waffle-tile"
                      aria-current={isCurrent ? "page" : undefined}
                      disabled={isBlocked}
                      title={
                        isCurrent
                          ? `${t.label} — you are here`
                          : isBlocked
                            ? `${t.label} — available once the organization is known`
                            : t.description || t.label
                      }
                      onClick={() => go(t)}
                    >
                      <span className={`dex-tile-icon dex-tint-${t.color}`}>
                        <Icon name={t.icon} />
                        {isCurrent ? (
                          <span className="dex-badge dex-badge-active">
                            <Icon name="check" />
                          </span>
                        ) : t.isExternal ? (
                          <span className="dex-badge dex-badge-ext">
                            <Icon name="external" />
                          </span>
                        ) : null}
                      </span>
                      <span className="dex-waffle-label">{t.label}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          ))}

          <div className="dex-waffle-foot">
            <button
              type="button"
              className="dex-menu-row"
              onClick={() => {
                setOpen(false)
                if (onOrgSwitch?.()) return
                const optimize = (hosts.optimize || "").replace(/\/$/, "")
                window.open(`${optimize}/dashboard`, "_blank", "noopener,noreferrer")
              }}
            >
              <Icon name="building" />
              <span>{org ? `Switch organization — ${orgLabel()}` : "Switch organization"}</span>
              {orgSwitchIsExternal && (
                <span className="dex-ext">
                  <Icon name="external" />
                </span>
              )}
            </button>
            {!org?.id && (
              <div className="dex-degraded" role="status">
                No organization selected yet — apps that open in an organization are
                unavailable until one is.
              </div>
            )}
            {degraded && (
              <div className="dex-degraded" role="status">
                Showing a built-in app list — the platform registry was unreachable.
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  )
}
